import { BookOpen, ExternalLink, NotebookPen } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { PilotGuideHint } from "../../../lib/callPilot";

/**
 * The handbook guides this call is about.
 *
 * We write down how every service is delivered, and this is the one screen where
 * that writing meets the moment it was written for: somebody with a customer on
 * the line, being asked how we do a thing. More than one card at a time is the
 * normal case, not a failure to choose - a call about moving a site touches the
 * migration procedure and the backup one, and hiding the second would be hiding
 * half of what we know.
 *
 * ⚠ A NEW TAB, NOT A ROUTE CHANGE. The call window is a scrim modal in a portal
 * (`CallWindow.tsx`), so a `<Link>` would navigate the app UNDER a cover the
 * operator cannot see through, and the guide they clicked would only appear
 * after they hang up. `target="_blank"` is the only way this link reaches the
 * reader while the call it belongs to is still running.
 *
 * The title is the server's, taken from our own `KbArticle` row rather than from
 * the model, so the link always lands on a page that exists. Only `why` is the
 * model's, and it is the part that a stored summary could never say: which
 * sentence in THIS conversation the guide answers.
 */
export function GuideList({
  guides,
  onAddToNote,
}: {
  guides: PilotGuideHint[];
  onAddToNote: (text: string) => void;
}) {
  const t = useT();
  if (guides.length === 0) return null;

  return (
    <section className="mt-4">
      <h4 className="crm-cp-h">{t("calls.pilot.guides")}</h4>
      <ul className="mt-2 flex flex-col gap-2">
        {guides.map((guide) => (
          <li key={guide.slug} className="crm-cp-guide">
            <BookOpen className="mt-[3px] size-3 shrink-0 opacity-70" />
            <span className="min-w-0 flex-1">
              <a
                className="crm-cp-guide-link"
                href={`/guides/${encodeURIComponent(guide.slug)}`}
                target="_blank"
                rel="noreferrer noopener"
                title={t("calls.pilot.openGuide")}
              >
                {guide.title}
                <ExternalLink className="size-3" />
              </a>
              {guide.why ? <span className="crm-cp-why block">{guide.why}</span> : null}
            </span>
            <button
              type="button"
              className="crm-cp-add is-inline"
              onClick={() => onAddToNote(`${guide.title}: /guides/${guide.slug}`)}
              title={t("calls.pilot.addToNote")}
              aria-label={t("calls.pilot.addToNote")}
            >
              <NotebookPen className="size-3" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
